import { defineStore } from 'pinia';
import type { IConversation } from './chatStore';

export interface MinimizedChat {
  conversationId: string;
  conversation: IConversation;
  /** Route to restore to, e.g. /messages/:id */
  restoreRoute: string;
  /** Where user came from before minimize — if from list, go to /messages; otherwise router.back() */
  cameFromList: boolean;
  /** Unread count accumulated while minimized */
  unreadCount: number;
}

export const useMinimizedChatsStore = defineStore('minimizedChats', {
  state: () => ({
    chats: [] as MinimizedChat[],
    /** Set briefly after a restore so the messages page can play the expand animation */
    lastRestoredId: null as string | null,
  }),

  getters: {
    isMinimized: (state) => (conversationId: string) => {
      return state.chats.some(c => c.conversationId === conversationId);
    },

    getChat: (state) => (conversationId: string) => {
      return state.chats.find(c => c.conversationId === conversationId);
    },
  },

  actions: {
    minimize(conversation: IConversation, cameFromList: boolean) {
      // Don't add duplicates
      if (this.chats.some(c => c.conversationId === conversation._id)) return;

      this.chats.push({
        conversationId: conversation._id,
        conversation,
        restoreRoute: `/messages/${conversation._id}`,
        cameFromList,
        unreadCount: 0,
      });
    },

    restore(conversationId: string) {
      const idx = this.chats.findIndex(c => c.conversationId === conversationId);
      if (idx === -1) return null;
      const chat = this.chats[idx];
      this.chats.splice(idx, 1);
      this.lastRestoredId = conversationId;
      return chat;
    },

    clearLastRestored() {
      this.lastRestoredId = null;
    },

    incrementUnread(conversationId: string) {
      const chat = this.chats.find(c => c.conversationId === conversationId);
      if (chat) chat.unreadCount++;
    },

    clearUnread(conversationId: string) {
      const chat = this.chats.find(c => c.conversationId === conversationId);
      if (chat) chat.unreadCount = 0;
    },
  },

  persist: {
    key: 'minimizedChats',
    storage: localStorage,
    pick: ['chats'],
  },
});
